import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/Auth.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
const INSTAGRAM_CLIENT_ID = process.env.REACT_APP_INSTAGRAM_CLIENT_ID || '';
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Decode Google JWT token to extract user info
 */
function decodeGoogleToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const decoded = JSON.parse(atob(parts[1]));
    return {
      email: decoded.email,
      name: decoded.name,
      picture: decoded.picture,
    };
  } catch (error) {
    console.error('Failed to decode Google token:', error);
    return null;
  }
}

function LoginContent() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const setError = useAuthStore((state) => state.setError);
  const setLoading = useAuthStore((state) => state.setLoading);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [googleInitialized, setGoogleInitialized] = useState(false);
  const [oauthTimeout, setOauthTimeout] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    // Clear any existing timeout
    if (oauthTimeout) {
      clearTimeout(oauthTimeout);
      setOauthTimeout(null);
    }

    try {
      setLoading(true);
      setError(null);

      // Decode the Google token to get email and name
      const googleData = decodeGoogleToken(credentialResponse.credential);
      if (!googleData || !googleData.email) {
        throw new Error('Failed to decode Google token');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          googleToken: credentialResponse.credential,
          email: googleData.email,
          name: googleData.name || '',
          phone: '',
          gender: '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Google authentication failed');
      }

      setToken(data.token);
      setUser(data.user);

      // Redirect to home after successful OAuth login
      navigate('/');
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(err.message || 'Google authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Load Google Identity Services script
  useEffect(() => {
    // Clear any stuck loading state on mount
    setLoading(false);
    setError(null);

    if (!GOOGLE_CLIENT_ID) {
      return;
    }

    // Initialize Google Sign-In function
    const initializeGoogleSignIn = () => {
      if (!window.google?.accounts?.id || !GOOGLE_CLIENT_ID) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleSuccess,
          cancel_on_tap_outside: true,
          auto_select: false,
        });
        setGoogleInitialized(true);
        console.log('Google Sign-In initialized successfully');
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
        setError('Failed to initialize Google Sign-In. Please refresh the page.');
      }
    };

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogleSignIn();
      return;
    }

    // Load the Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait a bit for the library to be ready
      const checkGoogleReady = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleReady);
          initializeGoogleSignIn();
        }
      }, 100);

      // Stop checking after 5 seconds
      setTimeout(() => {
        clearInterval(checkGoogleReady);
      }, 5000);
    };
    script.onerror = () => {
      console.error('Failed to load Google Sign-In script');
      setError('Failed to load Google Sign-In. Please check your internet connection.');
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Cancel any ongoing Google OAuth prompts
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.cancel();
        } catch (err) {
          // Ignore errors during cleanup
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Facebook SDK
  useEffect(() => {
    if (FACEBOOK_APP_ID && !window.FB) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      (function(d, s, id) {
        var js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s); js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode.insertBefore(js, fjs);
      }(document, 'script', 'facebook-jssdk'));
    }
  }, []);

  const handleInstagramLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Instagram Basic Display API - redirect to Instagram authorization
      const redirectUri = `${window.location.origin}/auth/instagram/callback`;
      const instagramAuthUrl = `https://api.instagram.com/oauth/authorize?client_id=${INSTAGRAM_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user_profile,user_media&response_type=code`;
      
      if (!INSTAGRAM_CLIENT_ID) {
        throw new Error('Instagram login is not configured. Please contact support.');
      }

      // Store current page for redirect after auth
      sessionStorage.setItem('instagram_redirect', window.location.pathname);
      window.location.href = instagramAuthUrl;
    } catch (err) {
      console.error('Instagram login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!FACEBOOK_APP_ID || !window.FB) {
        throw new Error('Facebook login is not configured. Please contact support.');
      }

      window.FB.login(async (fbResponse) => {
        if (fbResponse.authResponse) {
          try {
            // Get user info from Facebook
            window.FB.api('/me', { fields: 'name,email' }, async (userInfo) => {
              if (userInfo.error) {
                throw new Error(userInfo.error.message || 'Failed to get Facebook user info');
              }

              const response = await fetch(`${API_BASE_URL}/api/auth/facebook`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  facebookToken: fbResponse.authResponse.accessToken,
                  email: userInfo.email || '',
                  name: userInfo.name || '',
                  phone: '',
                  gender: '',
                }),
              });

              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || data.message || 'Facebook authentication failed');
              }

              setToken(data.token);
              setUser(data.user);

              // Redirect to home after successful OAuth login
              navigate('/');
            });
          } catch (err) {
            console.error('Facebook OAuth error:', err);
            setError(err.message);
            setLoading(false);
          }
        } else {
          setError('Facebook login was cancelled');
          setLoading(false);
        }
      }, { scope: 'email,public_profile' });
    } catch (err) {
      console.error('Facebook login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Log In</h1>

        {error && <div className="auth-error">{error}</div>}
        {isLoading && (
          <div className="auth-info">
            <p>Waiting for sign-in...</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
            />
          </div>

          <button type="submit" className="btn-submit" disabled={isLoading}>
            {isLoading ? 'Logging In...' : 'Log In'}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <div className="oauth-buttons">
          {GOOGLE_CLIENT_ID ? (
            <button
              type="button"
              className="btn-google"
              onClick={() => {
                try {
                  setLoading(true);
                  setError(null);

                  // Set a 30-second timeout to prevent stuck loading state
                  const timeout = setTimeout(() => {
                    console.warn('Google OAuth timeout - resetting loading state');
                    setLoading(false);
                    setError('Google sign-in took too long. Please try again.');
                  }, 30000);

                  setOauthTimeout(timeout);

                  if (!googleInitialized || !window.google?.accounts?.id) {
                    clearTimeout(timeout);
                    setLoading(false);
                    setError('Google Sign-In is not ready. Please refresh the page.');
                    return;
                  }

                  // Show Google sign-in prompt
                  window.google.accounts.id.prompt((notification) => {
                    if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                      // Prompt was not shown or user skipped it
                      clearTimeout(timeout);
                      setOauthTimeout(null);
                      setLoading(false);
                      setError('Google sign-in was cancelled or blocked. Please try again.');
                    }
                    // Note: We don't clear loading here if sign-in started successfully
                    // It will be cleared in handleGoogleSuccess or error handler
                  });
                } catch (err) {
                  console.error('Google Sign-In error:', err);
                  setError('Failed to start Google Sign-In. Please try again.');
                  setLoading(false);
                  if (oauthTimeout) {
                    clearTimeout(oauthTimeout);
                    setOauthTimeout(null);
                  }
                }
              }}
              disabled={isLoading || !googleInitialized}
            >
              {isLoading ? 'Signing in...' : googleInitialized ? 'Sign In with Google' : 'Loading Google...'}
            </button>
          ) : (
            <p className="oauth-note">Google Sign-In not configured</p>
          )}

          {INSTAGRAM_CLIENT_ID ? (
            <button
              type="button"
              className="btn-instagram"
              onClick={handleInstagramLogin}
              disabled={isLoading}
            >
              Sign in with Instagram
            </button>
          ) : null}

          {FACEBOOK_APP_ID ? (
            <button
              type="button"
              className="btn-facebook"
              onClick={handleFacebookLogin}
              disabled={isLoading}
            >
              Sign in with Facebook
            </button>
          ) : null}
        </div>

        <p className="auth-link">
          Don't have an account? <Link to="/register">Sign Up</Link>
        </p>
      </div>
    </div>
  );
}

export default function Login() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginContent />
    </GoogleOAuthProvider>
  );
}
